/**
 * `useFiltresUrl` — l'URL est la source de persistance de l'état de vue
 * (plan v3, phase 5.4).
 *
 * Remplace les hacks `sessionStorage` + `pendingScrollY` + `saveNavigationState` :
 * l'état de vue n'est pas un état de session à restaurer à la main, c'est une
 * adresse. Conséquences gratuites : coller une URL reproduit la vue, le retour
 * navigateur fonctionne, et rien n'est à sauvegarder avant de naviguer.
 *
 * Répartition (§0.7) : ce hook décide *quoi* filtrer, TanStack exécute le
 * filtrage. Il ne remplace pas `columnFilters`, il l'alimente.
 */
import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router";
import type { ColumnFiltersState } from "@tanstack/react-table";
import { enTexte } from "@repo/utils";

/**
 * Lit et écrit un jeu de paramètres d'URL typé par ses valeurs par défaut.
 *
 * Une valeur égale à son défaut est **retirée** de l'URL : les liens restent
 * courts et une vue par défaut a une adresse unique.
 */
export function useFiltresUrl<T extends Record<string, string>>(
  defauts: T,
): [T, (maj: Partial<T>) => void] {
  const [params, setParams] = useSearchParams();

  const valeurs = useMemo(() => {
    const sortie = { ...defauts };
    for (const cle of Object.keys(defauts) as (keyof T)[]) {
      const brut = params.get(String(cle));
      if (brut !== null) sortie[cle] = brut as T[keyof T];
    }
    return sortie;
  }, [params, defauts]);

  const definir = useCallback(
    (maj: Partial<T>) => {
      setParams(
        (courant) => {
          const suivant = new URLSearchParams(courant);
          for (const [cle, valeur] of Object.entries(maj)) {
            if (valeur === undefined || valeur === defauts[cle]) {
              suivant.delete(cle);
            } else {
              suivant.set(cle, String(valeur));
            }
          }
          return suivant;
        },
        // `replace` : filtrer n'est pas naviguer. Sans ça, chaque frappe
        // empilerait une entrée d'historique.
        { replace: true },
      );
    },
    [setParams, defauts],
  );

  return [valeurs, definir];
}

/** Un paramètre d'URL branché sur une colonne du `DataTable`. */
export interface ChampFiltreUrl {
  /** `id` de la colonne filtrée. */
  colonne: string;
  /** Valeurs multiples, sérialisées en CSV (`statut=actif,brouillon`). */
  multi?: boolean;
}

/**
 * Traduit des paramètres d'URL en `columnFilters` TanStack, et inversement
 * (plan v3, phase 6).
 *
 * Écrit une fois ici parce que les trois écrans à filtres feraient sinon la
 * même conversion à la main, chacun avec sa convention de sérialisation.
 * Répartition (§0.7) : l'URL porte l'intention, TanStack filtre.
 *
 * Cohabite avec un autre `useFiltresUrl` sur la même URL (l'onglet actif, par
 * exemple) : chaque écriture fusionne dans les paramètres existants.
 */
export function useFiltresColonnesUrl(
  champs: Record<string, ChampFiltreUrl>,
): [ColumnFiltersState, (filtres: ColumnFiltersState) => void] {
  const [params, setParams] = useSearchParams();

  const filtres = useMemo(() => {
    const sortie: ColumnFiltersState = [];
    for (const [cle, champ] of Object.entries(champs)) {
      const brut = params.get(cle);
      if (brut === null || brut === "") continue;
      sortie.push({
        id: champ.colonne,
        value: champ.multi ? brut.split(",").filter(Boolean) : brut,
      });
    }
    return sortie;
  }, [params, champs]);

  const definir = useCallback(
    (suivants: ColumnFiltersState) => {
      setParams(
        (courant) => {
          const params = new URLSearchParams(courant);
          for (const [cle, champ] of Object.entries(champs)) {
            const filtre = suivants.find((f) => f.id === champ.colonne);
            const valeur = Array.isArray(filtre?.value)
              ? (filtre.value as unknown[]).map(enTexte).join(",")
              : enTexte(filtre?.value);
            // Une valeur vide n'est pas un filtre : elle sort de l'URL, ce qui
            // garde une vue non filtrée à une seule adresse.
            if (valeur === "") params.delete(cle);
            else params.set(cle, valeur);
          }
          return params;
        },
        { replace: true },
      );
    },
    [setParams, champs],
  );

  return [filtres, definir];
}
